// =============================================================
// carwash.js — Car Wash Storefront Logic
// Version: 2.0 — 7-part booking tag format
// booking:{id}:{service}:{date}:{time}:{status}:{plan-type}
// =============================================================

// -------------------------------------------------------------
// SECTION 1 — PARSERS
// -------------------------------------------------------------

function parseBookingTag(tag) {
  const parts = tag.split(':');
  if (parts.length !== 7 || parts[0] !== 'booking') return null;
  return {
    raw:      tag,
    id:       parts[1],
    service:  parts[2],
    date:     parts[3],
    time:     parts[4].replace('-', ':'),  // "14-30" → "14:30"
    timeRaw:  parts[4],
    status:   parts[5],
    planType: parts[6],
  };
}

function parsePlanTag(tag) {
  const parts = tag.split(':');
  if (parts.length !== 5 || parts[0] !== 'plan') return null;
  return {
    raw:       tag,
    type:      parts[1],
    expiry:    parts[2],
    total:     parseInt(parts[3], 10),
    remaining: parseInt(parts[4], 10),
  };
}

function getCustomerTags() {
  const el = document.getElementById('carwash-customer-data');
  if (!el) return [];
  try {
    return el.dataset.tags.split('||').map(t => t.trim()).filter(Boolean);
  } catch { return []; }
}

function getBookings(tags) {
  return tags
    .filter(t => t.startsWith('booking:'))
    .map(parseBookingTag)
    .filter(Boolean)
    .sort((a, b) => b.id.localeCompare(a.id)); // newest first
}

function getPlan(tags) {
  const t = tags.find(t => t.startsWith('plan:'));
  return t ? parsePlanTag(t) : null;
}

// -------------------------------------------------------------
// SECTION 2 — LABEL MAPS (add your handles here)
// -------------------------------------------------------------

const SERVICE_LABELS = {
  'basic-exterior-wash': 'Basic Exterior Wash',
  'interior-deep-clean': 'Interior Deep Clean',
  'premium-detail': 'Premium Detail',
  'ppf-suv': 'PPF SUV',
};

const PLAN_LABELS = {
  'basic-monthly': 'Basic Monthly',
  'pro-monthly': 'Pro Monthly',
  'unlimited-monthly': 'Unlimited Monthly',
  'basic-annual': 'Basic Annual',
  'pro-annual': 'Pro Annual',
  'unlimited-annual': 'Unlimited Annual',
};

const PLAN_PAGES = {
  'gold-monthly':   '/pages/gold-plan',
  'silver-monthly': '/pages/silver-plan',
  'pro-monthly':    '/pages/pro-plan',
};

function serviceLabel(handle) {
  return SERVICE_LABELS[handle]
    ?? handle.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function planLabel(handle) {
  return PLAN_LABELS[handle]
    ?? handle.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric'
  });
}

// -------------------------------------------------------------
// SECTION 3 — CANCEL BUTTON RENDERER (Task 12)
// -------------------------------------------------------------

function renderCancelButton(booking) {
  const isTerminal = ['cancelled', 'completed'].includes(booking.status);
  if (isTerminal) return '';

  const body    = `CANCEL:${booking.id}:${booking.service}:${booking.date}`;
  const redirect = window.location.pathname + '?cancelled=true';

  return `
    <form method="POST" action="/contact#contact_form" class="booking-cancel-form">
      <input type="hidden" name="form_type"        value="contact">
      <input type="hidden" name="utf8"             value="✓">
      <input type="hidden" name="contact[subject]" value="CANCEL_BOOKING">
      <input type="hidden" name="contact[body]"    value="${body}">
      <input type="hidden" name="redirect"         value="${redirect}">
      <button type="submit" class="booking-cancel-btn">
        Cancel Booking
      </button>
    </form>
  `;
}

// -------------------------------------------------------------
// SECTION 4 — MY BOOKINGS CARDS (Tasks 11 + 12)
// -------------------------------------------------------------

function renderBookingCards() {
  const container = document.getElementById('my-bookings-list');
  if (!container) return;

  const tags     = getCustomerTags();
  const bookings = getBookings(tags);
  const plan     = getPlan(tags);

  if (bookings.length === 0) {
    container.innerHTML = '<p class="carwash-empty">No bookings found.</p>';
    return;
  }

  container.innerHTML = bookings.map(b => `
    <div class="booking-card booking-card--${b.status}" data-booking-id="${b.id}">
      <div class="booking-card__header">
        <span class="booking-card__id">${b.id}</span>
        <span class="booking-card__status booking-card__status--${b.status}">
          ${b.status}
        </span>
      </div>
      <div class="booking-card__body">
        <div class="booking-card__row">
          <span class="booking-card__label">Service</span>
          <span class="booking-card__value">${serviceLabel(b.service)}</span>
        </div>
        <div class="booking-card__row">
          <span class="booking-card__label">Date</span>
          <span class="booking-card__value">${formatDate(b.date)}</span>
        </div>
        <div class="booking-card__row">
          <span class="booking-card__label">Time</span>
          <span class="booking-card__value">${b.time}</span>
        </div>
        <div class="booking-card__row">
          <span class="booking-card__label">Plan</span>
          <span class="booking-card__value">${planLabel(b.planType)}</span>
        </div>
        ${plan ? `
        <div class="booking-card__row">
          <span class="booking-card__label">Washes left</span>
          <span class="booking-card__value">${plan.remaining} / ${plan.total}</span>
        </div>` : ''}
      </div>
      <div class="booking-card__actions">
        ${renderCancelButton(b)}
      </div>
    </div>
  `).join('');
}

// -------------------------------------------------------------
// SECTION 5 — TRACKING CARDS (Task 13)
// -------------------------------------------------------------

function renderTrackingCards() {
  const container = document.getElementById('my-tracking-list');
  if (!container) return;

  const tags     = getCustomerTags();
  const bookings = getBookings(tags).filter(b =>
    ['confirmed', 'in-progress', 'ready'].includes(b.status)
  );

  if (bookings.length === 0) {
    container.innerHTML = '<p class="carwash-empty">No active bookings to track.</p>';
    return;
  }

  const STATUS_STEPS = ['confirmed', 'in-progress', 'ready', 'completed'];

  container.innerHTML = bookings.map(b => {
    const stepIndex = STATUS_STEPS.indexOf(b.status);
    const steps = STATUS_STEPS.map((s, i) => `
      <div class="tracking-step ${i <= stepIndex ? 'tracking-step--done' : ''}">
        <div class="tracking-step__dot"></div>
        <span class="tracking-step__label">${s}</span>
      </div>
    `).join('');

    return `
      <div class="tracking-card" data-booking-id="${b.id}">
        <div class="tracking-card__header">
          <strong>${serviceLabel(b.service)}</strong>
          <span class="tracking-card__plan">${planLabel(b.planType)}</span>
        </div>
        <div class="tracking-card__meta">
          ${formatDate(b.date)} at ${b.time}
        </div>
        <div class="tracking-steps">${steps}</div>
      </div>
    `;
  }).join('');
}

// -------------------------------------------------------------
// SECTION 6 — MANAGE PLAN BUTTON (Task 14)
// -------------------------------------------------------------

function renderManagePlanButton() {
  const btn = document.getElementById('manage-plan-btn');
  if (!btn) return;

  const tags = getCustomerTags();
  const plan = getPlan(tags);

  if (!plan) { btn.style.display = 'none'; return; }

  const href = PLAN_PAGES[plan.type] ?? '/pages/subscription-plans';
  btn.setAttribute('href', href);
  btn.textContent = `Manage ${planLabel(plan.type)}`;
}

// -------------------------------------------------------------
// SECTION 7 — CANCELLED SUCCESS BANNER (Task 12)
// -------------------------------------------------------------

function renderCancelledBanner() {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('cancelled')) return;

  const banner = document.getElementById('carwash-cancelled-banner');
  if (banner) banner.style.display = 'block';
}

// -------------------------------------------------------------
// INIT
// -------------------------------------------------------------

document.addEventListener('DOMContentLoaded', () => {
  renderCancelledBanner();   // Task 12 — success message
  renderBookingCards();      // Tasks 11 + 12
  renderTrackingCards();     // Task 13
  renderManagePlanButton();  // Task 14
});
